/**
 * order-status-meta.ts  —  Status → next-step label + hint (R1, Phase 2 redesign)
 *
 * Maps each OrderStatus to a human-readable next-step label and an optional
 * contextual hint line shown in the amber dashed HintLine component.
 *
 * Keys are i18n references resolved by the caller with t(key).
 * Pairs with getPrimaryTransition(status) for the button action target.
 */

import type { OrderStatus } from "@/lib/api/types";
import { getPrimaryTransition } from "./orderQuickActions";
import type { OrderTransition } from "./orderStatusMachine";
import { keys } from "@/lib/i18n/portal";

export type StatusMeta = {
  /** i18n key for the "next step" action label shown on the dark button */
  nextLabelKey: string;
  /** i18n key for the amber hint line; null when no hint is needed */
  hintKey: string | null;
  /** The primary transition (action target + variant), or null for terminal states */
  transition: OrderTransition | null;
};

/**
 * Returns the display-layer status meta for a given OrderStatus.
 * Uses getPrimaryTransition() for the action target so the button and the
 * status-machine stay in sync — this layer only adds the label + hint copy.
 */
export function getStatusMeta(status: OrderStatus): StatusMeta {
  const transition = getPrimaryTransition(status);

  switch (status) {
    // ── Seller must review payment proof ──────────────────────────────────
    case "pending_payment_verification":
      return {
        nextLabelKey: keys.orders.metaNextReviewPayment,
        hintKey: keys.orders.metaHintPendingPaymentVerification,
        transition,
      };

    // ── COD order waiting for manual seller confirmation ──────────────────
    case "pending_seller_confirmation":
      return {
        nextLabelKey: keys.orders.metaNextConfirmOrder,
        hintKey: keys.orders.metaHintPendingSellerConfirmation,
        transition,
      };

    // ── Underpaid — partial amount received ───────────────────────────────
    case "paid_less_than_required":
      return {
        nextLabelKey: keys.orders.metaNextReviewPayment,
        hintKey: keys.orders.metaHintPaidLessThanRequired,
        transition,
      };

    // ── Waiting on buyer to pay (no seller action available) ──────────────
    case "pending_buyer_payment":
      return {
        nextLabelKey: keys.orders.metaNextViewOrder,
        hintKey: keys.orders.metaHintPendingBuyerPayment,
        transition,
      };

    // ── Waiting on buyer to verify their phone ────────────────────────────
    case "pending_buyer_verification":
      return {
        nextLabelKey: keys.orders.metaNextViewOrder,
        hintKey: keys.orders.metaHintPendingBuyerVerification,
        transition,
      };

    // ── Draft: seller-created; no public buyer action yet ─────────────────
    case "draft":
      return {
        nextLabelKey: keys.orders.metaNextViewOrder,
        hintKey: null,
        transition,
      };

    // ── Confirmed — seller starts preparing ───────────────────────────────
    case "confirmed":
      return {
        nextLabelKey: keys.orders.metaNextStartPreparing,
        hintKey: keys.orders.metaHintConfirmed,
        transition,
      };

    // ── Preparing — hand to courier ───────────────────────────────────────
    case "preparing":
      return {
        nextLabelKey: keys.orders.metaNextMarkShipped,
        hintKey: keys.orders.metaHintPreparing,
        transition,
      };

    // ── Shipped — mark as delivered ───────────────────────────────────────
    case "shipped":
      return {
        nextLabelKey: keys.orders.metaNextMarkDelivered,
        hintKey: keys.orders.metaHintShipped,
        transition,
      };

    // ── Terminal states — no forward action ───────────────────────────────
    case "delivered":
    case "cancelled":
    case "declined":
      return {
        nextLabelKey: keys.orders.metaNextViewOrder,
        hintKey: null,
        transition,
      };
  }
}
